import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import useVisitStore from '../../stores/visitStore';
import useAppointmentStore from '../../stores/appointmentStore';
import { usePermission, useScope } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';
import SmartTimeInput from './SmartTimeInput';
import DayTimeline, { fmtMin, durationOf, parseHHMM, effectiveStatus } from './DayTimeline';
import useProcedureStore from '../../stores/procedureStore';
import { excludeLabTests } from '../../constants/procedures';
import patientPlanApi from '../../services/api/patientPlanApi';
import { SPECIALIZATION_LABELS, STAFF_ROLES } from '../../constants/staff';
import { ADMISSION_ACCESS_ERROR_CODE, ADMISSION_ACCESS_ERROR_COPY } from '../../constants/admission';
import { mapDoctorsResponse } from '../../services/api/visitApi';
import useAssignableProviders, { DOCTORS_LOADING_COPY, DOCTORS_LOAD_ERROR_COPY, doctorsEmptyCopy } from '../../hooks/useAssignableProviders';
import { PhoneButton } from '../common/PhoneButton';
import DatePicker from '../common/DatePicker';
import ABHASection from '../abha/ABHASection';
import useAbdmEnabled from '../../hooks/useAbdmEnabled';
import { normalizeGender } from '../../utils/gender';
import { abhaStatusMeta, abhaToneColors } from './abhaStatusMeta';
import { abhaTelemetry } from './abhaTelemetry';
import AbhaBadge from './AbhaBadge';
import { buildAbhaLinkRequest, abhaAutofillPatch, isLastNameRequired, isLastNameLocked, isFirstNameLocked, formatPatientName, isMaskedAbhaNumber, SELF_TAKEN_COPY, isSelfTakenError } from '../../services/api/abhaProfile';
import { linkABHAToPatient } from '../../services/api/abhaApi';
import AbhaLinkConfirmDialog from '../abha/AbhaLinkConfirmDialog';
import AbhaAlreadyLinkedNotice from '../abha/AbhaAlreadyLinkedNotice';
import useAbhaDuplicateGuard, { ABHA_ALREADY_LINKED_CODE, ABHA_ALREADY_LINKED_NOTE } from '../../hooks/useAbhaDuplicateGuard';
import AgeInput from '../common/AgeInput';
import AdmittedElsewhereLabel from '../common/AdmittedElsewhereLabel';
import { VERIFY_ABHA, ADD_ABHA } from '../../constants/abdmCopy';

// Default booked block length by type — editable by the booker.
const DUR_DEFAULT = { CONSULTATION: 5, PROCEDURE: 45 };

/**
 * ═══════════════════════════════════════════════════════════════════════
 * BookAppointmentForm — Multi-step appointment booking (mirrors RegisterVisitForm)
 *
 * Step 1 (mobile):       Enter 10-digit mobile → auto-lookup
 * Step 2a (select):      Known patients → select who the appointment is for
 *                         + "Add Family Member" button
 * Step 2b (new_patient): New patient / family member → simplified form
 * Step 3 (appointment):  Doctor (pre-populated) + Time + Date + Notes → Submit
 *
 * Fields (mandatory): patient_mobile, doctor, appointment_time
 * Fields (conditional): first_name, last_name (new patient only)
 * Fields (optional):  age, dob, gender, notes, date (defaults to today)
 * ═══════════════════════════════════════════════════════════════════════
 */

const NAME_LOCKED_STYLE = { opacity: 0.75, cursor: 'not-allowed' };   // R139: name read-only while an ABHA is attached
const RELATIONS = [
  { value: 'SELF', label: 'Self' },
  { value: 'WIFE', label: 'Wife' },
  { value: 'HUSBAND', label: 'Husband' },
  { value: 'CHILD', label: 'Child' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'FRIEND', label: 'Friend' },
  { value: 'OTHER', label: 'Other' },
];

const INITIAL_FORM = {
  patient_id: '',
  first_name: '',
  last_name: '',
  patient_mobile: '',
  doctor_member_id: '',
  timeRaw: '',
  appointment_date: '',
  relation: 'SELF',
  age: '',
  dob: '',
  gender: '',
  notes: '',
  appointment_type: '',
  procedure_id: '',
  procedure_name: '',
  procedures: [],
  duration_minutes: null,
};

function todayISO() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

export default function BookAppointmentForm({ onSuccess, onCancel }) {
  const canManage = usePermission('MANAGE_APPOINTMENT');
  const canLinkAbha = usePermission('ABHA_LINK');
  const canVerifyAbha = usePermission('ABHA_VERIFY');
  const canCreateAbha = usePermission('ABHA_CREATE');
  const { isDark } = useTheme();
  const { isScoped, isAdmin: viewerIsAdmin, viewerDeptIds } = useScope();
  // Same display-only predicate as Register Visit / Admit Patient: a non-admin with >=1 department.
  const viewerIsDeptScoped = !viewerIsAdmin && Array.isArray(viewerDeptIds) && viewerDeptIds.length > 0;
  const { searchPatient, patientLookup, clearPatientLookup, dailyVisits, fetchDailyVisits } = useVisitStore();
  const { book, loading, error, createdAppointment, clearCreated, clearError, fetchDoctorDayBookings, dayBookings, dayBlocks, fetchDoctorBlocks, cancel, update } = useAppointmentStore();
  const { procedures, fetchProcedures } = useProcedureStore();

  const [form, setForm] = useState({ ...INITIAL_FORM, appointment_date: todayISO() });
  const [view, setView] = useState('mobile'); // mobile → select / new_patient → appointment
  const [selectedPatient, setSelectedPatient] = useState(null);
  // ABHA link/verify modal: null, or { patientId, abhaStatus, initialMode, patientName }.
  const [abhaFlow, setAbhaFlow] = useState(null);
  // "Is this the same person?" gate for an EXISTING patient: { patientId, snapshot }. Nothing links
  // to the record until the dialog confirms it.
  const [linkConfirm, setLinkConfirm] = useState(null);
  const { show: abdmOn } = useAbdmEnabled();
  const abhaDup = useAbhaDuplicateGuard();
  const { check: checkAbhaDup, clear: clearAbhaDup, fromError: abhaFromError } = abhaDup;
  const [abhaLinkNote, setAbhaLinkNote] = useState('');   // new patient booked, ABHA (year-only) could not link
  const [relationHint, setRelationHint] = useState(false);   // number already has a SELF patient → Relation must be chosen
  const [abhaOldUhid, setAbhaOldUhid] = useState('');    // old record left unchanged by "Register as new patient"
  // New-patient ABHA: verified BEFORE the patient exists → holds the profile snapshot so we can
  // auto-fill demographics now and link it after booking creates the patient_id.
  const [newPatientAbha, setNewPatientAbha] = useState(null);
  const [resolvedTime24, setResolvedTime24] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [procOptions, setProcOptions] = useState([]);   // open plan items ∪ receptionist-allowed catalog
  const [clashAppt, setClashAppt] = useState(null);     // soft overlap warning from DayTimeline
  // ── Manage existing appointment (reschedule / cancel) from the booking screen ──
  const [editingId, setEditingId] = useState(null);     // appointment_id being edited (reuses the form)
  const [manageBusy, setManageBusy] = useState(false);  // cancel-in-progress
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const debounceRef = useRef(null);
  const mobileLookupRef = useRef(null);
  const durTouchedRef = useRef(false);                  // did the booker override the duration default?
  const [pastTimeError, setPastTimeError] = useState(null);
  // The server refused the booking: the doctor is outside the viewer's departments.
  const [providerOutOfScope, setProviderOutOfScope] = useState(false);

  // Doctor picker = the server-scoped assignable list (admission-access S10/S14).
  // Never the org-wide roster: while loading the list is empty.
  const { providers, loading: doctorsLoading, error: doctorsError } = useAssignableProviders(STAFF_ROLES.DOCTOR);
  const doctors = useMemo(() => mapDoctorsResponse(providers), [providers]);

  // Fetch today's daily visits for queue count
  useEffect(() => {
    if (form.appointment_date === todayISO()) {
      fetchDailyVisits({ date: todayISO() });
    }
  }, [form.appointment_date, fetchDailyVisits]);

  // Auto-assign doctor when only one is available
  useEffect(() => {
    if (doctors.length === 1) {
      setForm((f) => f.doctor_member_id ? f : { ...f, doctor_member_id: doctors[0].doctor_id });
    }
  }, [doctors]);

  // Fetch the selected doctor's day for the DayTimeline (scoped per doctor):
  // both appointments and the doctor's calendar blocks for that date.
  useEffect(() => {
    if (form.doctor_member_id && form.appointment_date) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchDoctorDayBookings(form.doctor_member_id, form.appointment_date);
        fetchDoctorBlocks(form.doctor_member_id, form.appointment_date);
      }, 300);
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [form.doctor_member_id, form.appointment_date, fetchDoctorDayBookings, fetchDoctorBlocks]);

  // Re-fetch the doctor's day on entering the appointment view (same doctor+date edge case)
  useEffect(() => {
    if (view === 'appointment' && form.doctor_member_id && form.appointment_date) {
      fetchDoctorDayBookings(form.doctor_member_id, form.appointment_date);
      fetchDoctorBlocks(form.doctor_member_id, form.appointment_date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Build the procedure dropdown options when type=Procedure:
  //   open plan items for this patient  ∪  receptionist-allowed catalog procedures
  useEffect(() => {
    if (form.appointment_type !== 'PROCEDURE') return undefined;
    let alive = true;
    (async () => {
      if (!procedures.length) await fetchProcedures();
      const catalog = excludeLabTests(useProcedureStore.getState().procedures);
      const catalogById = {};
      catalog.forEach((p) => { catalogById[p.procedure_id] = p; });

      const catalogOpts = catalog
        .filter((p) => p.receptionist_allowed && p.active !== false)
        .map((p) => ({
          procedure_id: p.procedure_id,
          name: p.name,
          duration: p.typical_duration_minutes || 0,
          source: 'catalog',
        }));

      // Patient's OPEN doctor-recommended procedures (not yet done), keyed by
      // patient_id. Uses /plans/open-items (includes accepted-but-unscheduled
      // recommendations) — NOT /plans/active, which hides them behind a sitting gate.
      let attachedOpts = [];
      if (form.patient_id) {
        const res = await patientPlanApi.listOpenProcedureItems(form.patient_id);
        if (res.success && res.data) {
          (res.data.items || []).forEach((it) => {
            attachedOpts.push({
              procedure_id: it.procedure_id,
              name: it.name || it.procedure_id,
              duration: it.duration_minutes || 0,
              source: 'attached',
            });
          });
        }
      }

      // Merge by procedure_id — attached wins for label; duration falls back to catalog.
      const byId = {};
      [...attachedOpts, ...catalogOpts].forEach((o) => {
        const dur = o.duration || catalogById[o.procedure_id]?.typical_duration_minutes || 0;
        const existing = byId[o.procedure_id];
        if (!existing) byId[o.procedure_id] = { ...o, duration: dur };
        else if (o.source === 'attached') byId[o.procedure_id] = { ...o, duration: dur || existing.duration };
      });
      if (alive) setProcOptions(Object.values(byId));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.appointment_type, form.patient_id]);

  // Show success popup when appointment is created
  useEffect(() => {
    if (createdAppointment) setShowSuccess(true);
  }, [createdAppointment]);

  // When patientLookup changes, transition view
  useEffect(() => {
    if (patientLookup === null) return; // no search yet
    if (Array.isArray(patientLookup) && patientLookup.length > 0) {
      setView('select');
    } else if (form.patient_mobile.length === 10) {
      // Not found → new patient
      setView('new_patient');
      setForm((f) => ({ ...f, relation: 'SELF' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientLookup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPatientLookup();
      clearError();
    };
  }, [clearPatientLookup, clearError]);

  // ── Handlers ─────────────────────────────────────────────────────

  const handleMobileChange = useCallback((value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 10);
    setForm((f) => ({ ...f, patient_mobile: cleaned, patient_id: '' }));
    setSelectedPatient(null);
    clearAbhaDup();
    setNewPatientAbha(null);   // new number → discard any held new-patient ABHA
    setRelationHint(false);
    setAbhaOldUhid('');
    setView('mobile');
    if (mobileLookupRef.current) clearTimeout(mobileLookupRef.current);
    if (cleaned.length === 10) {
      mobileLookupRef.current = setTimeout(() => {
        searchPatient(cleaned);
      }, 500);
    } else {
      clearPatientLookup();
    }
  }, [searchPatient, clearPatientLookup, clearAbhaDup]);

  const handleSelectPatient = useCallback((patient) => {
    clearAbhaDup();
    setSelectedPatient(patient);
    setRelationHint(false);
    setAbhaOldUhid('');
    setForm((f) => ({
      ...f,
      patient_id: patient.patient_id,
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      relation: patient.relation || 'SELF',
      age: patient.age || '',
      dob: patient.dob || '',
      gender: normalizeGender(patient.gender),
    }));
    setView('appointment');
  }, [clearAbhaDup]);

  // "Open patient" on the already-linked notice: book against that existing patient instead.
  const handleOpenDuplicatePatient = () => abhaDup.open(handleSelectPatient);
  // Open the ABHA link/verify modal for the selected patient. `intent` = 'create' | 'verify'
  // (from abhaStatusMeta.ctaIntent) → ABHASection.initialMode, so it skips the chooser.
  const openAbhaFlow = useCallback((patient, intent) => {
    if (!patient?.patient_id) return;
    abhaTelemetry(intent === 'verify' ? 'ABHA_VERIFY_STARTED' : 'ABHA_LINK_STARTED',
                  { intent, fromStatus: patient.abha_status || 'NOT_LINKED' });
    setAbhaFlow({
      patientId: patient.patient_id,
      abhaStatus: patient.abha_status || 'NOT_LINKED',
      initialMode: intent,
      patientName: formatPatientName(patient),
    });
  }, []);

  // A verified ABHA for an EXISTING patient is never linked straight away: the "same person?" dialog
  // runs first (it calls the link endpoint itself). The badge flips only AFTER that link succeeds.
  const persistAbhaLink = useCallback((snapshot) => {
    const req = buildAbhaLinkRequest(abhaFlow?.patientId, snapshot);
    setAbhaFlow(null);
    if (req) setLinkConfirm({ patientId: req.patientId, snapshot });   // else nothing to link (shouldn't happen post-verify)
  }, [abhaFlow]);

  const handleAbhaLinked = useCallback(() => {
    abhaTelemetry('ABHA_VERIFIED_FROM_APPOINTMENT');
    setSelectedPatient((p) => (p ? { ...p, abha_status: 'VERIFIED' } : p));
    setLinkConfirm(null);
  }, []);

  // Open the ABHA link/verify workflow for a NEW patient (no patient_id yet). On completion we
  // auto-fill demographics and hold the snapshot; the actual link happens after booking.
  const openNewPatientAbhaFlow = useCallback(() => {
    abhaTelemetry('ABHA_LINK_STARTED', { intent: 'new_patient', fromStatus: 'NOT_LINKED' });
    setAbhaFlow({
      patientId: '',                 // none yet — ABHASection skips the record fetch
      abhaStatus: 'NOT_LINKED',
      initialMode: undefined,        // show the create/verify chooser
      isNewPatient: true,
      patientName: formatPatientName({ first_name: form.first_name, last_name: form.last_name }),
    });
  }, [form.first_name, form.last_name]);

  const handleAbhaComplete = useCallback((snapshot) => {
    if (!snapshot) return;
    // NEW-patient flow: no patient_id exists yet. Auto-fill demographics from the verified ABHA
    // profile and retain the snapshot; linking happens after book() creates the patient.
    if (abhaFlow?.isNewPatient) {
      abhaTelemetry('ABHA_VERIFIED_FROM_APPOINTMENT', { newPatient: true });
      setForm((f) => ({ ...f, ...abhaAutofillPatch(snapshot, f) }));
      setNewPatientAbha(snapshot);
      setAbhaFlow(null);
      return;
    }
    persistAbhaLink(snapshot);
  }, [abhaFlow, persistAbhaLink]);

  const closeAbhaFlow = useCallback(() => {
    setAbhaFlow(null);
  }, []);

  const handleAddFamilyMember = useCallback(() => {
    const patients = Array.isArray(patientLookup) ? patientLookup : [];
    const hasSelf = patients.some((p) => (p.relation || 'SELF') === 'SELF');
    setSelectedPatient(null);
    setRelationHint(hasSelf);
    setAbhaOldUhid('');
    setForm((f) => ({
      ...f,
      patient_id: '',
      first_name: '',
      last_name: '',
      relation: hasSelf ? '' : 'SELF',
      age: '',
      dob: '',
      gender: '',
    }));
    setView('new_patient');
  }, [patientLookup]);

  // Different person: new patient prefilled from the ABHA; it links after booking (existing path).
  const handleAbhaRegisterNew = useCallback(({ oldUhid }) => {
    const snapshot = linkConfirm?.snapshot;
    handleAddFamilyMember();
    const hasSelf = (Array.isArray(patientLookup) ? patientLookup : []).some((p) => (p.relation || 'SELF') === 'SELF');
    setRelationHint(hasSelf);
    if (hasSelf) setForm((f) => ({ ...f, relation: '' }));
    if (snapshot) {
      setForm((f) => ({ ...f, ...abhaAutofillPatch(snapshot, f) }));
      setNewPatientAbha(snapshot);
    }
    setAbhaOldUhid(oldUhid || '');
    setLinkConfirm(null);
  }, [linkConfirm, handleAddFamilyMember, patientLookup]);

  const handleConfirmNewPatient = useCallback(() => {
    const errors = {};
    if (!form.first_name.trim()) errors.first_name = 'First name is required';
    if (!form.last_name.trim() && isLastNameRequired(newPatientAbha)) errors.last_name = 'Last name is required';
    if (!form.relation) errors.relation = 'Relation is required';
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setView('appointment');
  }, [form.first_name, form.last_name, form.relation, newPatientAbha]);

  const handleTimeResolve = useCallback((display, hour24, time24) => {
    setResolvedTime24(time24);

    // Block past time if today
    if (time24 && form.appointment_date === todayISO()) {
      const now = new Date();
      const nowIST = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const [h, m] = time24.split(':').map(Number);
      const selectedMinutes = h * 60 + m;
      const currentMinutes = nowIST.getHours() * 60 + nowIST.getMinutes();
      if (selectedMinutes < currentMinutes) {
        const nowH = nowIST.getHours() % 12 || 12;
        const nowM = String(nowIST.getMinutes()).padStart(2, '0');
        const nowPeriod = nowIST.getHours() >= 12 ? 'PM' : 'AM';
        setPastTimeError(`Cannot book in the past. Current time is ${nowH}:${nowM} ${nowPeriod}.`);
      } else {
        setPastTimeError(null);
      }
    } else {
      setPastTimeError(null);
    }
  }, [form.appointment_date]);

  // ── Timeline appointments: resolve LIVE visit status on each row ──
  // Prefer the server's visit_status (reflects completion AND resume); fall
  // back to a same-session dailyVisits overlay for instant local updates.
  const timelineAppointments = useMemo(() => {
    const appts = (dayBookings || []).map((a) => {
      let status = effectiveStatus(a);
      if (!a.visit_status && a.status === 'CHECKED_IN' && a.visit_id) {
        const v = (dailyVisits || []).find((x) => (x.id || x.visit_id) === a.visit_id);
        if (v && (v.status === 'IN_PROGRESS' || v.status === 'COMPLETED')) status = v.status;
      }
      return { ...a, status };
    });
    // Merge in only the doctor's BLOCKING calendar blocks — they render red so
    // staff see the doctor is blocked (booking still allowed; soft warning on
    // overlap). Non-blocking blocks are the doctor's private note: hidden here.
    return [...appts, ...(dayBlocks || []).filter((b) => b.blocking)];
  }, [dayBookings, dayBlocks, dailyVisits]);

  // ── Existing appointment(s) for THIS patient with the selected doctor+date ──
  // Derived from the resolved timeline rows. A COMPLETED visit no longer counts
  // (re-booking is fine); if it's later resumed it reappears here automatically.
  const existingForPatient = useMemo(() => {
    return timelineAppointments.filter((b) => {
      if (b.appointment_id === editingId) return false; // the row being edited isn't a "duplicate"
      if (b.status === 'COMPLETED') return false;
      // Cancelled / expired / no-show appointments are not live duplicates — they
      // render struck-through but must not block re-booking the same patient.
      if (b.status === 'CANCELLED' || b.status === 'EXPIRED' || b.status === 'NO_SHOW') return false;
      if (form.patient_id && b.patient_id) return b.patient_id === form.patient_id;
      return form.patient_mobile && b.patient_mobile === form.patient_mobile;
    });
  }, [timelineAppointments, form.patient_id, form.patient_mobile, editingId]);

  // ── Queue count for today (only for selected doctor) ──
  const todayQueueCount = useMemo(() => {
    if (form.appointment_date !== todayISO() || !form.doctor_member_id) return null;
    const activeVisits = (dailyVisits || []).filter((v) => {
      const docId = v.doctor_member_id || v.visit_doctor_id;
      const status = v.status;
      return docId === form.doctor_member_id && (status === 'QUEUE' || status === 'IN_PROGRESS');
    });
    return activeVisits.length;
  }, [dailyVisits, form.appointment_date, form.doctor_member_id]);

  const handleChange = useCallback((field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setValidationErrors((e) => ({ ...e, [field]: undefined }));
  }, []);

  // ── Appointment type / procedure / duration ──────────────────────────
  const handleSelectType = useCallback((type) => {
    durTouchedRef.current = false;
    setForm((f) => ({
      ...f,
      appointment_type: type,
      duration_minutes: DUR_DEFAULT[type],
      procedure_id: '',
      procedure_name: '',
      procedures: [],
    }));
    setValidationErrors((e) => ({ ...e, appointment_type: undefined }));
  }, []);

  // Apply a new procedures list to the form: refresh the joined legacy fields
  // and auto-seed the total to the sum (unless the booker overrode it).
  const applyProcedures = (f, procedures) => {
    const total = procedures.reduce((s, p) => s + (p.duration || 0), 0);
    const next = {
      ...f,
      procedures,
      procedure_id: procedures[0]?.procedure_id || '',
      procedure_name: procedures.map((p) => p.name).join(', '),
    };
    if (!durTouchedRef.current) next.duration_minutes = total > 0 ? total : DUR_DEFAULT.PROCEDURE;
    return next;
  };

  const addProcedure = useCallback((procId) => {
    if (!procId) return;
    const opt = procOptions.find((o) => o.procedure_id === procId);
    if (!opt) return;
    setForm((f) => {
      if (f.procedures.some((p) => p.procedure_id === procId)) return f;
      return applyProcedures(f, [...f.procedures, { procedure_id: opt.procedure_id, name: opt.name, duration: opt.duration || 0 }]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procOptions]);

  const removeProcedure = useCallback((procId) => {
    setForm((f) => applyProcedures(f, f.procedures.filter((p) => p.procedure_id !== procId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDuration = useCallback((mins) => {
    const d = Math.max(5, Math.min(600, mins || 5));
    durTouchedRef.current = true;
    setForm((f) => ({ ...f, duration_minutes: d }));
  }, []);

  // DayTimeline → set the start time (same path SmartTimeInput resolve uses)
  const handlePickTime = useCallback((hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const display = `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
    handleChange('timeRaw', display);
    handleTimeResolve(display, h, hhmm);
  }, [handleChange, handleTimeResolve]);

  const effectiveDuration = form.duration_minutes
    || DUR_DEFAULT[form.appointment_type]
    || DUR_DEFAULT.CONSULTATION;

  // ── Edit an existing appointment by reusing this booking form ──
  const startEdit = useCallback((appt) => {
    setConfirmCancelId(null);
    setEditingId(appt.appointment_id);
    durTouchedRef.current = true; // keep the stored duration; don't auto-reset
    let procedures = Array.isArray(appt.procedures)
      ? appt.procedures.map((p) => ({ procedure_id: p.procedure_id, name: p.name, duration: p.duration_minutes || 0 }))
      : [];
    if (!procedures.length && appt.procedure_id) {
      // legacy single-procedure row → seed one chip
      procedures = [{ procedure_id: appt.procedure_id, name: appt.procedure_name || appt.procedure_id, duration: appt.duration_minutes || 0 }];
    }
    setForm((f) => ({
      ...f,
      appointment_type: appt.appointment_type || 'CONSULTATION',
      procedures,
      procedure_id: appt.procedure_id || (procedures[0]?.procedure_id || ''),
      procedure_name: appt.procedure_name || procedures.map((p) => p.name).join(', '),
      duration_minutes: appt.duration_minutes || null,
      notes: appt.notes || '',
      appointment_date: appt.appointment_date || f.appointment_date,
      timeRaw: appt.appointment_time ? fmtMin(parseHHMM(appt.appointment_time)) : f.timeRaw,
    }));
    if (appt.appointment_time) {
      const [h] = appt.appointment_time.split(':').map(Number);
      handleTimeResolve(fmtMin(parseHHMM(appt.appointment_time)), h, appt.appointment_time);
    }
  }, [handleTimeResolve]);

  const cancelEdit = useCallback(() => setEditingId(null), []);

  const handleCancelExisting = useCallback(async (id) => {
    setConfirmCancelId(null);
    setManageBusy(true);
    try {
      await cancel(id);
      if (form.doctor_member_id && form.appointment_date) {
        fetchDoctorDayBookings(form.doctor_member_id, form.appointment_date);
      }
    } catch (_) { /* error surfaced via store */ }
    finally { setManageBusy(false); }
  }, [cancel, fetchDoctorDayBookings, form.doctor_member_id, form.appointment_date]);

  // ── Full reset back to mobile step ──
  const resetToMobile = useCallback(() => {
    // Preserve doctor for single-doctor clinics so the day timeline stays active
    const autoDoc = doctors.length === 1 ? doctors[0].doctor_id : '';
    setForm({ ...INITIAL_FORM, appointment_date: todayISO(), doctor_member_id: autoDoc });
    clearPatientLookup();
    setSelectedPatient(null);
    setNewPatientAbha(null);
    setRelationHint(false);
    setAbhaOldUhid('');
    setView('mobile');
    setValidationErrors({});
    setResolvedTime24(null);
    setPastTimeError(null);
    setClashAppt(null);
    setProcOptions([]);
    setEditingId(null);
    setConfirmCancelId(null);
    setManageBusy(false);
    setProviderOutOfScope(false);
    durTouchedRef.current = false;
  }, [clearPatientLookup, doctors]);

  const handleBack = useCallback(() => {
    clearAbhaDup(); // also cancels a pending Open patient
    if (view === 'appointment') {
      if (selectedPatient) {
        setView('select');
        setSelectedPatient(null);
        setForm((f) => ({ ...f, patient_id: '' }));
      } else if (Array.isArray(patientLookup) && patientLookup.length > 0) {
        setView('select');
      } else {
        resetToMobile();
      }
    } else if (view === 'new_patient') {
      if (Array.isArray(patientLookup) && patientLookup.length > 0) {
        setView('select');
      } else {
        resetToMobile();
      }
    } else if (view === 'select') {
      resetToMobile();
    }
  }, [view, selectedPatient, patientLookup, resetToMobile, clearAbhaDup]);

  const validate = useCallback(() => {
    const errors = {};
    if (!form.patient_mobile || form.patient_mobile.length !== 10) errors.patient_mobile = 'Valid 10-digit mobile required';
    if (!form.doctor_member_id) errors.doctor_member_id = 'Select a doctor';
    if (!resolvedTime24) {
      errors.timeRaw = 'Valid time is required';
    } else if (pastTimeError) {
      errors.timeRaw = pastTimeError;
    }
    if (!form.appointment_type) errors.appointment_type = 'Select Consultation or Procedure';
    // Don't allow a second appointment for the same patient+doctor+date — manage the existing one instead.
    if (existingForPatient.length > 0) errors.duplicate = 'Patient already has an appointment with this doctor on this date';
    if (!form.patient_id) {
      if (!form.first_name.trim()) errors.first_name = 'First name is required';
      if (!form.last_name.trim() && isLastNameRequired(newPatientAbha)) errors.last_name = 'Last name is required';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form, resolvedTime24, pastTimeError, existingForPatient, newPatientAbha]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    clearError();
    setProviderOutOfScope(false);
    if (!validate()) return;

    const patientName = selectedPatient
      ? formatPatientName(selectedPatient)
      : formatPatientName(form);

    const isProc = form.appointment_type === 'PROCEDURE';
    const procPayload = isProc && form.procedures.length
      ? form.procedures.map((p) => ({ procedure_id: p.procedure_id, name: p.name, duration_minutes: p.duration || null }))
      : (isProc ? [] : null);

    // Existing patient picked → book against exactly that record. Anyone else is a NEW person:
    // always force a new patient + UHID (with a Relation) so the backend can never reuse another
    // person's record on the same mobile — with or without an ABHA.
    const existingPatientId = selectedPatient?.patient_id || form.patient_id || '';
    const forceNewPatient = !existingPatientId;
    if (forceNewPatient && !editingId && !form.relation) {
      setRelationHint(true);
      setView('new_patient');
      return;
    }

    // R22 one-ABHA-one-patient: a NEW patient carrying a verified ABHA must not be created if that
    // ABHA already belongs to a patient — show the notice and create nothing.
    if (forceNewPatient && !editingId && newPatientAbha?.abhaNumber && !isMaskedAbhaNumber(newPatientAbha.abhaNumber)
      && await checkAbhaDup(newPatientAbha.abhaNumber)) return;
    clearAbhaDup();

    try {
      if (editingId) {
        // Edit the same appointment — reuse the form, persist via update().
        await update(editingId, {
          appointment_date: form.appointment_date || todayISO(),
          appointment_time: resolvedTime24,
          doctor_member_id: form.doctor_member_id,
          appointment_type: form.appointment_type,
          duration_minutes: effectiveDuration,
          procedure_id: isProc ? (form.procedure_id || '') : null,
          procedure_name: isProc ? (form.procedure_name || '') : null,
          procedures: procPayload,
          notes: form.notes.trim() || null,
        });
        setEditingId(null);
      } else {
        const booked = await book({
          patient_name: patientName,
          patient_mobile: form.patient_mobile,
          doctor_member_id: form.doctor_member_id,
          appointment_time: resolvedTime24,
          appointment_date: form.appointment_date || todayISO(),
          appointment_type: form.appointment_type,
          duration_minutes: effectiveDuration,
          procedure_id: isProc ? (form.procedure_id || null) : null,
          procedure_name: isProc ? (form.procedure_name || null) : null,
          procedures: isProc && form.procedures.length ? procPayload : null,
          age: (selectedPatient ? selectedPatient.age : form.age) || null,
          dob: (selectedPatient ? selectedPatient.dob : form.dob) || null,
          gender: normalizeGender(selectedPatient ? selectedPatient.gender : form.gender) || null,
          notes: form.notes.trim() || null,
          ...(existingPatientId ? { patient_id: existingPatientId } : {}),
          ...(forceNewPatient ? { create_new_patient: true, relation: form.relation } : {}),
          // R88 — server-side duplicate guard (new patient only).
          ...(forceNewPatient && newPatientAbha?.abhaNumber && !isMaskedAbhaNumber(newPatientAbha.abhaNumber)
            ? { abha_number: newPatientAbha.abhaNumber } : {}),
        });
        // New-patient ABHA: booking created the patient — now link the verified ABHA + persist the
        // full profile. Non-blocking (booking already succeeded); backend link_abha is idempotent.
        if (forceNewPatient && newPatientAbha) {
          const pid = booked?.patient?.patient_id || booked?.appointment?.patient_id
            || booked?.patient_id;
          const req = pid && buildAbhaLinkRequest(pid, newPatientAbha);
          if (req) {
            try { await linkABHAToPatient(req); } catch (e) {
              if ((e?.linkCode || e?.abhaCode) === ABHA_ALREADY_LINKED_CODE) setAbhaLinkNote(ABHA_ALREADY_LINKED_NOTE);
              else if (e?.linkCode === 'ABHA_DOB_UNAVAILABLE') setAbhaLinkNote('ABHA could not be linked because the ABHA has only a birth year.');
            }   // non-blocking
          }
          setNewPatientAbha(null);
        }
      }
      // Re-fetch the doctor's day so the timeline reflects the change.
      if (form.doctor_member_id && form.appointment_date) {
        fetchDoctorDayBookings(form.doctor_member_id, form.appointment_date);
      }
    } catch (err) {
      if (await abhaFromError(err)) {
        // R88: server refused a second patient for an already-linked ABHA — notice replaces the banner.
        clearError();
        return;
      }
      if (isSelfTakenError(err)) {
        // Backend allows one SELF per mobile: back to the Relation picker, inline (no banner).
        clearError();
        setForm((f) => ({ ...f, relation: '' }));
        setRelationHint(true);
        setView('new_patient');
        return;
      }
      if (err?.data?.detail?.code === 'LAST_NAME_REQUIRED' || err?.code === 'LAST_NAME_REQUIRED') {
        clearError();
        setValidationErrors({ last_name: 'Last name is required' });
        setView('new_patient');
        return;
      }
      // Other errors are shown from the store; this one maps to PO copy.
      setProviderOutOfScope(err?.data?.detail?.code === ADMISSION_ACCESS_ERROR_CODE.PROVIDER_OUT_OF_SCOPE);
    }
  }, [form, selectedPatient, resolvedTime24, effectiveDuration, editingId, book, update, clearError, validate, fetchDoctorDayBookings, newPatientAbha, checkAbhaDup, clearAbhaDup, abhaFromError]);

  const handleDismissSuccess = useCallback(() => {
    setShowSuccess(false);
    clearCreated();
    if (isScoped && onSuccess) return onSuccess();
    resetToMobile();
  }, [clearCreated, isScoped, onSuccess, resetToMobile]);

  const handleCancel = useCallback(() => {
    resetToMobile();
    clearCreated();
    clearError();
    if (onCancel) onCancel();
  }, [resetToMobile, clearCreated, clearError, onCancel]);

  const dismissError = useCallback(() => {
    clearError();
    setProviderOutOfScope(false);
  }, [clearError]);

  if (!canManage) return null;

  const errorText = providerOutOfScope
    ? ADMISSION_ACCESS_ERROR_COPY[ADMISSION_ACCESS_ERROR_CODE.PROVIDER_OUT_OF_SCOPE]
    : error;

  const patients = Array.isArray(patientLookup) ? patientLookup : [];
  const hasSelf = patients.some((p) => (p.relation || 'SELF') === 'SELF');
  const patientName = selectedPatient
    ? formatPatientName(selectedPatient)
    : formatPatientName(form);

  // ── Styles ─────────────────────────────────────────────────────────
  const card = {
    width: '100%',
    borderRadius: '1rem',
    overflow: 'hidden',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.85)',
    boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.3)' : '0 4px 24px rgba(0,0,0,0.06)',
  };

  const inputBase = (hasError) => ({
    width: '100%',
    padding: '0.75rem 0.875rem',
    borderRadius: '0.75rem',
    border: `1.5px solid ${hasError ? '#ef4444' : isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
    background: isDark ? 'rgba(255,255,255,0.06)' : '#f9fafb',
    color: isDark ? '#f5f5f7' : '#111827',
    fontSize: '0.9375rem',
    outline: 'none',
    transition: 'border 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
  });

  const labelStyle = {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: isDark ? 'rgba(255,255,255,0.5)' : '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '0.5rem',
  };

  const errorTextStyle = { margin: '0.375rem 0 0', fontSize: '0.75rem', color: '#ef4444' };
  const mutedNoteStyle = { margin: '0.375rem 0 0', fontSize: '0.8125rem', color: isDark ? '#6b7280' : '#9ca3af' };

  const durStepBtn = {
    padding: '0.4375rem 0.75rem', border: 'none', cursor: 'pointer',
    background: isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6',
    color: isDark ? '#e8e9ed' : '#374151', fontSize: '1.125rem', fontWeight: 600, lineHeight: 1,
  };

  // selected doctor label + clash end-time helper for the timeline
  const selectedDoctorDoc = (doctors || []).find((d) => d.doctor_id === form.doctor_member_id);
  const selectedDoctorLabel = selectedDoctorDoc
    ? `Dr. ${selectedDoctorDoc.display_name || selectedDoctorDoc.name || `${selectedDoctorDoc.first_name || ''} ${selectedDoctorDoc.last_name || ''}`.trim()}`
    : 'Doctor';
  const clashEndLabel = (a) => {
    const s = parseHHMM(a?.appointment_time);
    return s == null ? '' : fmtMin(s + durationOf(a));
  };
  const startMin24 = resolvedTime24
    ? parseInt(resolvedTime24.split(':')[0], 10) * 60 + parseInt(resolvedTime24.split(':')[1], 10)
    : null;

  const btnPrimary = (disabled) => ({
    width: '100%',
    padding: '0.875rem',
    borderRadius: '0.75rem',
    border: 'none',
    background: 'linear-gradient(135deg, #0d9488, #0d9488)',
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.9375rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'background 0.2s, opacity 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    boxShadow: '0 4px 14px rgba(13, 148, 136,0.3)',
  });

  const btnOutline = {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '0.75rem',
    border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
    background: 'transparent',
    color: isDark ? '#e8e9ed' : '#374151',
    fontWeight: 500,
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'background 0.2s, border-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  };

  return (
    <>
      {abhaLinkNote && (
        <div role="status" style={{
          position: 'fixed', left: '50%', bottom: '1.5rem', transform: 'translateX(-50%)', zIndex: 10003,
          display: 'flex', gap: '0.75rem', alignItems: 'center', padding: '0.75rem 1rem', borderRadius: '0.625rem',
          background: isDark ? '#78350f' : '#fffbeb', border: `1px solid ${isDark ? '#b45309' : '#f59e0b'}`,
          color: isDark ? '#fde68a' : '#92400e', fontSize: '0.8125rem', fontWeight: 600,
        }}>
          {abhaLinkNote}
          <button type="button" onClick={() => setAbhaLinkNote('')} style={{
            padding: '0.25rem 0.625rem', borderRadius: '0.375rem', cursor: 'pointer', fontWeight: 700,
            background: isDark ? '#1e293b' : '#fff', border: '1px solid #f59e0b', color: isDark ? '#fde68a' : '#92400e',
          }}>Dismiss</button>
        </div>
      )}
      {/* ── Success Popup ── */}
      {showSuccess && createdAppointment && (
        <SuccessPopup
          appointment={createdAppointment}
          isDark={isDark}
          onClose={handleDismissSuccess}
          isScoped={isScoped}
        />
      )}

      {/* ── ABHA link/verify modal — opened from the appointment card CTA, keeps booking context ── */}
      {linkConfirm && (
        <AbhaLinkConfirmDialog
          patientId={linkConfirm.patientId}
          snapshot={linkConfirm.snapshot}
          onLinked={handleAbhaLinked}
          onRegisterNew={handleAbhaRegisterNew}
          onCancel={() => setLinkConfirm(null)}
          onVerifyAgain={() => { setLinkConfirm(null); openAbhaFlow(selectedPatient, 'verify'); }}
        />
      )}
      {abhaFlow && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10001,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)', padding: '1rem', overflowY: 'auto',
        }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeAbhaFlow(); }}
        >
          <div style={{
            width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto',
            background: isDark ? '#1e293b' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
            borderRadius: '0.9rem', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.9rem 1.1rem',
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
            }}>
              <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: isDark ? '#f5f5f7' : '#111827' }}>
                {abhaFlow.initialMode === 'verify' ? VERIFY_ABHA : ADD_ABHA}
                {abhaFlow.patientName ? ` · ${abhaFlow.patientName}` : ''}
              </span>
              <button type="button" onClick={closeAbhaFlow} style={{
                width: '2rem', height: '2rem', borderRadius: '0.5rem',
                cursor: 'pointer',
                background: isDark ? '#374151' : '#f3f4f6',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
                color: isDark ? '#f5f5f7' : '#374151', fontSize: '1rem', fontWeight: 700,
              }} title="Close">✕</button>
            </div>
            <div style={{ padding: '1rem 1.1rem 1.25rem' }}>
              <ABHASection
                patientId={abhaFlow.patientId}
                abhaStatus={abhaFlow.abhaStatus}
                initialMode={abhaFlow.initialMode}
                defaultOpen
                hideHeader
                patientName={abhaFlow.patientName}
                defaultMobile={form.patient_mobile}
                abhaResult={null}
                onAbhaComplete={handleAbhaComplete}
              />
            </div>
          </div>
        </div>
      )}

      <div style={card}>
        {/* ── Header ── */}
        <div style={{
          background: 'linear-gradient(135deg, #0d9488 0%, #0d9488 100%)',
          padding: '1rem 1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
              {view !== 'mobile' && (
                <button onClick={handleBack} style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                  cursor: 'pointer', fontSize: '0.875rem', padding: '0.25rem 0.5rem',
                  borderRadius: '0.375rem', lineHeight: 1,
                }}>←</button>
              )}
              <span style={{ fontSize: '1.25rem' }}>📅</span>
              <h2 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '1.125rem' }}>Book Appointment</h2>
            </div>
            {onCancel && (
              <button onClick={handleCancel} style={{
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                cursor: 'pointer', fontSize: '0.875rem', padding: '0.25rem 0.625rem',
                borderRadius: '0.375rem',
              }}>✕</button>
            )}
          </div>
        </div>

        {/* ── Error Banner ── */}
        {errorText && (
          <div style={{
            padding: '0.75rem 1.5rem',
            background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
            borderBottom: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : '#fecaca'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>⚠️</span>
              <p style={{ margin: 0, fontSize: '0.875rem', color: isDark ? '#fca5a5' : '#991b1b' }}>{errorText}</p>
            </div>
            <button onClick={dismissError} style={{
              background: 'none', border: 'none', color: isDark ? '#fca5a5' : '#ef4444',
              cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500,
            }}>Dismiss</button>
          </div>
        )}

        {/* ── Body ── */}
        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

          {/* ═══ STEP 1: Mobile Number ═══ */}
          <div>
            <label style={labelStyle}>Mobile Number *</label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)',
                fontSize: '0.9375rem', fontWeight: 500, color: isDark ? '#9ca3af' : '#6b7280',
              }}>+91</span>
              <input
                type="tel"
                inputMode="numeric"
                value={form.patient_mobile}
                onChange={(e) => handleMobileChange(e.target.value)}
                placeholder="Enter 10-digit mobile"
                style={{ ...inputBase(validationErrors.patient_mobile), paddingLeft: '3.25rem', fontSize: '1rem' }}
                maxLength={10}
                disabled={view !== 'mobile'}
                autoFocus
              />
              {view !== 'mobile' && (
                <button type="button"
                  onClick={resetToMobile}
                  style={{
                    position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)',
                    background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
                    color: isDark ? '#d1d5db' : '#374151', cursor: 'pointer', fontSize: '0.75rem',
                    fontWeight: 500, padding: '0.25rem 0.625rem', borderRadius: '0.375rem',
                  }}
                >Change</button>
              )}
            </div>
            {validationErrors.patient_mobile && <p style={errorTextStyle}>{validationErrors.patient_mobile}</p>}
          </div>

          {/* ═══ STEP 2a: Patient Selection (known patients) ═══ */}
          {view === 'select' && patients.length > 0 && (
            <div>
              <label style={labelStyle}>Who is this appointment for?</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {patients.map((p) => {
                  const name = formatPatientName(p) || 'Unknown';
                  const rel = p.relation || 'SELF';
                  const relLabel = RELATIONS.find((r) => r.value === rel)?.label || rel;
                  return (
                    <button key={p.patient_id} type="button" onClick={() => handleSelectPatient(p)} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.875rem 1.125rem', borderRadius: '0.75rem', width: '100%',
                      border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                      background: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
                      cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{
                          width: '2.5rem', height: '2.5rem', borderRadius: '50%', flexShrink: 0,
                          background: 'linear-gradient(135deg, #0d9488, #0d9488)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: '0.875rem', fontWeight: 700,
                        }}>{(p.first_name || '?')[0].toUpperCase()}</span>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>{name}</span>
                            {/* ABHA status — informational here (no CTA; still choosing the patient). */}
                            <AbhaBadge status={p.abha_status} isDark={isDark} />
                          </div>
                          <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '0.125rem' }}>
                            {relLabel}{p.age ? ` · Age ${p.age}` : ''}{p.gender ? ` · ${p.gender}` : ''}
                          </div>
                          <AdmittedElsewhereLabel patient={p} />
                        </div>
                      </div>
                      <span style={{
                        padding: '0.375rem 1rem', borderRadius: '9999px', fontSize: '0.75rem',
                        fontWeight: 600, background: 'linear-gradient(135deg, #0d9488, #0d9488)',
                        color: '#fff', flexShrink: 0,
                      }}>Select</span>
                    </button>
                  );
                })}
              </div>
              <button type="button" onClick={handleAddFamilyMember} style={{ ...btnOutline, marginTop: '1rem' }}>
                <span>👥</span> Add Family Member
              </button>
            </div>
          )}

          {/* ═══ STEP 2b: New Patient / Family Member Form ═══ */}
          {view === 'new_patient' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{
                padding: '0.625rem 1rem', borderRadius: '0.625rem',
                background: isDark ? 'rgba(13, 148, 136,0.08)' : 'rgba(13, 148, 136,0.04)',
                border: `1px solid ${isDark ? 'rgba(13, 148, 136,0.15)' : 'rgba(13, 148, 136,0.1)'}`,
                fontSize: '0.8125rem', color: '#0d9488', display: 'flex',
                alignItems: 'center', gap: '0.5rem', fontWeight: 500,
              }}>
                <span>👤</span>
                {patients.length > 0 ? 'Adding a family member to this number' : 'New patient — enter details'}
              </div>

              {/* ABHA (optional) — placed FIRST so staff verify before typing: on success the fields
                  below auto-fill from the ABHA profile; the ABHA links after booking. */}
              {abdmOn && (canLinkAbha || canCreateAbha) && (
                <div style={{
                  padding: '0.85rem 1rem', borderRadius: '0.75rem',
                  background: isDark ? 'rgba(16,185,129,0.08)' : '#f0fdf4',
                  border: `1px solid ${isDark ? 'rgba(16,185,129,0.3)' : '#bbf7d0'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
                }}>
                  {newPatientAbha ? (
                    <>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: isDark ? '#6ee7b7' : '#047857' }}>
                        ✓ ABHA verified{newPatientAbha.fullName ? ` · ${newPatientAbha.fullName}` : ''} — details auto-filled
                      </span>
                      <button type="button" onClick={() => setNewPatientAbha(null)} style={{
                        padding: '0.35rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 700,
                        cursor: 'pointer', background: isDark ? '#1e293b' : '#ffffff',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}`,
                        color: isDark ? '#e2e8f0' : '#475569',
                      }}>Clear</button>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: isDark ? '#cbd5e1' : '#475569' }}>
                        🪪 Has an ABHA? Verify to auto-fill details <span style={{ fontWeight: 400, opacity: 0.8 }}>(optional)</span>
                      </span>
                      <button type="button" onClick={openNewPatientAbhaFlow} style={{
                        padding: '0.5rem 0.9rem', borderRadius: '0.5rem', fontSize: '0.8125rem', fontWeight: 700,
                        cursor: 'pointer', flexShrink: 0,
                        background: isDark ? 'rgba(16,185,129,0.15)' : '#dcfce7',
                        border: `1px solid ${isDark ? 'rgba(16,185,129,0.5)' : '#86efac'}`,
                        color: isDark ? '#6ee7b7' : '#047857',
                      }}>{ADD_ABHA}</button>
                    </>
                  )}
                </div>
              )}

              {abhaOldUhid && (
                <div style={{
                  padding: '0.625rem 0.875rem', borderRadius: '0.625rem', fontSize: '0.8125rem',
                  background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}`,
                  color: isDark ? '#d1d5db' : '#334155',
                }}>
                  This will be a new patient with a new UHID. Old record ({abhaOldUhid}) is not changed.
                </div>
              )}

              {/* Name — 2 columns */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>First Name *</label>
                  <input type="text" value={form.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    disabled={isFirstNameLocked(newPatientAbha)}
                    placeholder="First name" style={{ ...inputBase(validationErrors.first_name), ...(isFirstNameLocked(newPatientAbha) ? NAME_LOCKED_STYLE : {}) }} autoFocus />
                  {validationErrors.first_name && <p style={errorTextStyle}>{validationErrors.first_name}</p>}
                </div>
                <div>
                  <label style={labelStyle}>Last Name {isLastNameRequired(newPatientAbha) ? '*' : ''}</label>
                  <input type="text" value={form.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    disabled={isLastNameLocked(newPatientAbha)}
                    placeholder="Last name" style={{ ...inputBase(validationErrors.last_name), ...(isLastNameLocked(newPatientAbha) ? NAME_LOCKED_STYLE : {}) }} />
                  {validationErrors.last_name && <p style={errorTextStyle}>{validationErrors.last_name}</p>}
                </div>
              </div>

              {/* Relation chips (shown when adding family member) */}
              {(patients.length > 0 || relationHint) && (
                <div>
                  <label style={labelStyle}>Relation *</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {RELATIONS.map((r) => {
                      const selected = form.relation === r.value;
                      const disabled = r.value === 'SELF' && hasSelf;
                      return (
                        <button key={r.value} type="button" disabled={disabled}
                          onClick={() => !disabled && handleChange('relation', r.value)}
                          style={{
                            padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.8125rem',
                            fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.35 : 1, transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                            border: selected ? '2px solid #0d9488' : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                            background: selected ? (isDark ? 'rgba(13, 148, 136,0.15)' : 'rgba(13, 148, 136,0.06)') : (isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb'),
                            color: selected ? '#0d9488' : isDark ? '#e8e9ed' : '#6b7280',
                          }}
                        >{r.label}</button>
                      );
                    })}
                  </div>
                  {relationHint && !form.relation && !validationErrors.relation && <p style={errorTextStyle}>{SELF_TAKEN_COPY}</p>}
                  {validationErrors.relation && <p style={errorTextStyle}>{validationErrors.relation}</p>}
                </div>
              )}

              {/* Age, DOB, Gender — optional for appointment booking */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Age <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                  <AgeInput value={form.age}
                    onChange={(age) => handleChange('age', age)}
                    inputStyle={inputBase(false)} />
                </div>
                <div>
                  <label style={labelStyle}>Date of Birth <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                  <input type="date" value={form.dob}
                    onChange={(e) => handleChange('dob', e.target.value)} style={inputBase(false)} />
                </div>
                <div>
                  <label style={labelStyle}>Gender <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                  <select value={form.gender}
                    onChange={(e) => handleChange('gender', e.target.value)} style={inputBase(false)}>
                    <option value="">Select</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>

              <button type="button" onClick={handleConfirmNewPatient} style={btnPrimary(false)}>
                ✓ Confirm & Continue
              </button>
            </div>
          )}

          {/* ═══ STEP 3: Appointment Details ═══ */}
          {view === 'appointment' && (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {abhaDup.duplicate && (
                <AbhaAlreadyLinkedNotice
                  patientName={abhaDup.duplicate.patientName}
                  uhid={abhaDup.duplicate.uhid}
                  patientId={abhaDup.duplicate.patientId}
                  onOpenPatient={handleOpenDuplicatePatient}
                  opening={abhaDup.opening}
                />
              )}
              {/* Patient summary card */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                padding: '1rem 1.125rem', borderRadius: '0.75rem',
                background: isDark ? 'rgba(13, 148, 136,0.08)' : 'rgba(13, 148, 136,0.04)',
                border: `1px solid ${isDark ? 'rgba(13, 148, 136,0.2)' : 'rgba(13, 148, 136,0.12)'}`,
              }}>
                <span style={{
                  width: '2.75rem', height: '2.75rem', borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #0d9488, #0d9488)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: '1rem', fontWeight: 700,
                }}>{(form.first_name || '?')[0].toUpperCase()}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
                    {patientName || (selectedPatient ? 'Existing patient' : 'New Patient')}
                  </div>
                  {selectedPatient && !selectedPatient.first_name && !selectedPatient.last_name && (
                    <div style={{ fontSize: '0.75rem', color: isDark ? '#fbbf24' : '#b45309' }}>Patient details could not be loaded.</div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', marginTop: '0.125rem' }}>
                    {RELATIONS.find((r) => r.value === form.relation)?.label || form.relation}
                    {form.age ? ` · Age ${form.age}` : ''}
                    {form.gender ? ` · ${form.gender}` : ''}
                    {' · '}{form.patient_mobile}
                  </div>
                </div>
                {selectedPatient && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem', flexShrink: 0 }}>
                    {/* ABHA health-identity state (orthogonal to "Existing"). */}
                    {selectedPatient.abha_status !== undefined && (
                      <AbhaBadge status={selectedPatient.abha_status} isDark={isDark} />
                    )}
                    {/* "Existing" kept as a secondary workflow indicator (patient already in system). */}
                    <span style={{
                      padding: '0.15rem 0.5rem', borderRadius: '9999px',
                      fontSize: '0.625rem', fontWeight: 600,
                      background: isDark ? 'rgba(148,163,184,0.15)' : 'rgba(100,116,139,0.1)',
                      color: isDark ? '#94a3b8' : '#64748b',
                    }}>Existing</span>
                  </div>
                )}
              </div>

              {/* ABHA action — shown only when the patient needs linking/verification. Opens the
                  ABHASection workflow inline (modal); appointment details stay intact. */}
              {abdmOn && selectedPatient && selectedPatient.abha_status !== undefined && abhaStatusMeta(selectedPatient.abha_status).needsAction
                && (abhaStatusMeta(selectedPatient.abha_status).ctaIntent === 'verify' ? canVerifyAbha : canLinkAbha) && (() => {
                const meta = abhaStatusMeta(selectedPatient.abha_status);
                const tone = abhaToneColors(meta.tone, isDark);
                return (
                  <button type="button"
                    onClick={() => openAbhaFlow(selectedPatient, meta.ctaIntent)}
                    style={{
                      alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.5rem 0.9rem', borderRadius: '0.5rem', cursor: 'pointer',
                      fontSize: '0.8125rem', fontWeight: 700,
                      background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`,
                    }}>
                    🪪 {meta.cta}
                  </button>
                );
              })()}

              {/* Editing-mode banner */}
              {editingId && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
                  padding: '0.5rem 0.875rem', borderRadius: '0.625rem',
                  border: `1px solid ${isDark ? 'rgba(59,130,246,0.4)' : 'rgba(59,130,246,0.4)'}`,
                  background: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff',
                }}>
                  <span style={{ fontSize: '0.75rem', color: isDark ? '#93c5fd' : '#1d4ed8', fontWeight: 600 }}>
                    ✏️ Editing this appointment — change anything and Save
                  </span>
                  <button type="button" onClick={cancelEdit} style={{
                    background: 'none', border: 'none', color: isDark ? '#93c5fd' : '#2563eb',
                    fontSize: '0.6875rem', fontWeight: 600, cursor: 'pointer',
                  }}>Cancel edit</button>
                </div>
              )}

              {/* Already-booked — manage the existing appointment in place (no duplicate) */}
              {existingForPatient.length > 0 && (
                <div style={{
                  borderRadius: '0.75rem',
                  border: `1.5px solid ${isDark ? 'rgba(245,158,11,0.5)' : '#f59e0b'}`,
                  background: isDark ? 'rgba(245,158,11,0.14)' : '#fffbeb',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.625rem 0.875rem',
                    background: isDark ? 'rgba(245,158,11,0.16)' : 'rgba(245,158,11,0.12)',
                    borderBottom: `1px solid ${isDark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.3)'}`,
                  }}>
                    <span style={{ fontSize: '1rem' }}>⚠️</span>
                    <span style={{ fontSize: '0.8125rem', color: isDark ? '#fcd34d' : '#92400e', fontWeight: 700 }}>
                      {(patientName || 'This patient')} already has {existingForPatient.length > 1 ? 'appointments' : 'an appointment'} with {selectedDoctorLabel} on {form.appointment_date === todayISO() ? 'today' : form.appointment_date}
                    </span>
                  </div>

                  <div style={{ padding: '0.625rem 0.875rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                    {existingForPatient.map((a) => {
                      const isConfirming = confirmCancelId === a.appointment_id;
                      const typeText = a.appointment_type === 'PROCEDURE'
                        ? (Array.isArray(a.procedures) && a.procedures.length
                            ? `Procedure: ${a.procedures.map((p) => p.name).filter(Boolean).join(', ')}`
                            : (a.procedure_name ? `Procedure: ${a.procedure_name}` : 'Procedure'))
                        : 'Consultation';
                      return (
                        <div key={a.appointment_id} style={{
                          borderRadius: '0.5rem',
                          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                          background: isDark ? 'rgba(0,0,0,0.15)' : '#ffffff',
                          padding: '0.5rem 0.625rem',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.75rem', color: isDark ? '#e8e9ed' : '#111827', fontWeight: 600 }}>
                              #{a.appointment_number} · {a.appointment_time ? fmtMin(parseHHMM(a.appointment_time)) : '—'}
                              <span style={{ fontWeight: 400, color: isDark ? '#9ca3af' : '#6b7280' }}> · {typeText}</span>
                            </span>
                            {!isConfirming && (
                              <span style={{ display: 'flex', gap: '0.375rem' }}>
                                <button type="button" disabled={manageBusy} onClick={() => startEdit(a)} style={{
                                  padding: '0.3rem 0.625rem', borderRadius: '0.375rem', cursor: 'pointer',
                                  border: '1px solid #3b82f644', background: '#3b82f615', color: '#3b82f6',
                                  fontSize: '0.6875rem', fontWeight: 600,
                                }}>✏️ Edit</button>
                                <button type="button" disabled={manageBusy} onClick={() => setConfirmCancelId(a.appointment_id)} style={{
                                  padding: '0.3rem 0.625rem', borderRadius: '0.375rem', cursor: 'pointer',
                                  border: '1px solid #ef444444', background: '#ef444415', color: '#ef4444',
                                  fontSize: '0.6875rem', fontWeight: 600,
                                }}>✕ Cancel</button>
                              </span>
                            )}
                          </div>

                          {/* Cancel confirm */}
                          {isConfirming && (
                            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '0.6875rem', color: isDark ? '#fca5a5' : '#991b1b' }}>Cancel this appointment?</span>
                              <button type="button" disabled={manageBusy} onClick={() => handleCancelExisting(a.appointment_id)} style={{
                                padding: '0.375rem 0.75rem', borderRadius: '0.5rem', border: 'none',
                                background: '#ef4444', color: '#fff', fontSize: '0.6875rem', fontWeight: 600, cursor: 'pointer',
                              }}>{manageBusy ? '…' : 'Yes, cancel'}</button>
                              <button type="button" onClick={() => setConfirmCancelId(null)} style={{
                                padding: '0.375rem 0.75rem', borderRadius: '0.5rem',
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
                                background: 'transparent', color: isDark ? '#9ca3af' : '#6b7280',
                                fontSize: '0.6875rem', cursor: 'pointer',
                              }}>Keep</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Doctor */}
              <div>
                <label style={labelStyle}>Consulting Doctor *</label>
                <select value={form.doctor_member_id}
                  onChange={(e) => handleChange('doctor_member_id', e.target.value)}
                  style={inputBase(validationErrors.doctor_member_id)}>
                  <option value="">Select Doctor</option>
                  {(doctors || []).map((doc) => {
                    const spec = doc.specialization ? ` — ${SPECIALIZATION_LABELS[doc.specialization] || doc.specialization}` : '';
                    return (
                      <option key={doc.doctor_id} value={doc.doctor_id}>
                        Dr. {doc.display_name || doc.name || `${doc.first_name || ''} ${doc.last_name || ''}`.trim()}{spec}
                        {doc.availability === 'on_hold' ? ' ⏸ On Hold' : doc.availability === 'closed_today' ? ' 🚫 Closed' : ''}
                      </option>
                    );
                  })}
                </select>
                {validationErrors.doctor_member_id && <p style={errorTextStyle}>{validationErrors.doctor_member_id}</p>}
                {doctorsLoading && <p style={mutedNoteStyle}>{DOCTORS_LOADING_COPY}</p>}
                {doctorsError && <p style={errorTextStyle}>{DOCTORS_LOAD_ERROR_COPY}</p>}
                {!doctorsLoading && !doctorsError && doctors.length === 0 && (
                  <p style={mutedNoteStyle}>{doctorsEmptyCopy(viewerIsDeptScoped)}</p>
                )}
                {/* Doctor availability warning */}
                {(() => {
                  if (!form.doctor_member_id) return null;
                  const doc = doctors.find((d) => d.doctor_id === form.doctor_member_id);
                  const avail = doc?.availability;
                  if (!avail || avail === 'available') return null;
                  const isHold = avail === 'on_hold';
                  const warningColor = isHold ? '#f59e0b' : '#ef4444';
                  const warningIcon = isHold ? '🟡' : '🔴';
                  const warningText = isHold
                    ? 'This doctor is currently On Hold. Appointment may need adjustment.'
                    : 'This doctor has Closed for Today. Appointments are not recommended.';
                  return (
                    <div style={{
                      marginTop: '0.375rem',
                      padding: '0.4375rem 0.625rem',
                      borderRadius: '0.5rem',
                      border: `1px solid ${isDark ? `${warningColor}30` : `${warningColor}25`}`,
                      background: isDark ? `${warningColor}10` : `${warningColor}08`,
                      display: 'flex', alignItems: 'flex-start', gap: '0.375rem',
                    }}>
                      <span style={{ fontSize: '0.75rem', flexShrink: 0, marginTop: '0.0625rem' }}>{warningIcon}</span>
                      <p style={{
                        margin: 0, fontSize: '0.75rem', lineHeight: 1.4,
                        color: warningColor, fontWeight: 500,
                      }}>{warningText}</p>
                    </div>
                  );
                })()}
              </div>

              {/* Appointment Type (required) — specialty-neutral */}
              <div>
                <label style={labelStyle}>Appointment Type *</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[{ v: 'CONSULTATION', l: '🩺 Consultation' }, { v: 'PROCEDURE', l: '📋 Procedure' }].map((t) => {
                    const sel = form.appointment_type === t.v;
                    return (
                      <button key={t.v} type="button" onClick={() => handleSelectType(t.v)} style={{
                        flex: 1, padding: '0.625rem 1rem', borderRadius: '9999px', fontSize: '0.875rem', fontWeight: 500,
                        cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                        border: sel ? '2px solid #0d9488' : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                        background: sel ? '#0d9488' : (isDark ? 'rgba(255,255,255,0.05)' : '#ffffff'),
                        color: sel ? '#fff' : (isDark ? '#e8e9ed' : '#374151'),
                      }}>{t.l}</button>
                    );
                  })}
                </div>
                {validationErrors.appointment_type && <p style={errorTextStyle}>{validationErrors.appointment_type}</p>}
              </div>

              {/* Procedures (optional, multiple) — open plan items ∪ receptionist-allowed catalog */}
              {form.appointment_type === 'PROCEDURE' && (() => {
                const addedIds = new Set(form.procedures.map((p) => p.procedure_id));
                const available = procOptions.filter((o) => !addedIds.has(o.procedure_id));
                return (
                  <div>
                    <label style={labelStyle}>Procedures <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional — add one or more)</span></label>
                    {/* Added procedure chips */}
                    {form.procedures.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.5rem' }}>
                        {form.procedures.map((p) => (
                          <span key={p.procedure_id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                            padding: '0.3125rem 0.5rem 0.3125rem 0.625rem', borderRadius: '9999px',
                            fontSize: '0.75rem', fontWeight: 500,
                            border: '1px solid #0d9488',
                            background: isDark ? 'rgba(13,148,136,0.15)' : 'rgba(13,148,136,0.08)',
                            color: '#0d9488',
                          }}>
                            {p.name}{p.duration ? ` · ${p.duration}m` : ''}
                            <button type="button" onClick={() => removeProcedure(p.procedure_id)} style={{
                              border: 'none', background: 'transparent', color: '#0d9488',
                              cursor: 'pointer', fontSize: '0.875rem', lineHeight: 1, padding: 0,
                            }} aria-label={`Remove ${p.name}`}>✕</button>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Add-a-procedure select (resets after each pick) */}
                    <select value="" onChange={(e) => { addProcedure(e.target.value); e.target.value = ''; }} style={inputBase(false)}>
                      <option value="">{form.procedures.length ? '+ Add another procedure' : 'Not sure yet — decide at visit'}</option>
                      {available.some((o) => o.source === 'attached') && (
                        <optgroup label="Open for this patient">
                          {available.filter((o) => o.source === 'attached').map((o) => (
                            <option key={`a-${o.procedure_id}`} value={o.procedure_id}>{o.name}{o.duration ? ` · ${o.duration} min` : ''}</option>
                          ))}
                        </optgroup>
                      )}
                      {available.some((o) => o.source === 'catalog') && (
                        <optgroup label="Receptionist-bookable">
                          {available.filter((o) => o.source === 'catalog').map((o) => (
                            <option key={`c-${o.procedure_id}`} value={o.procedure_id}>{o.name}{o.duration ? ` · ${o.duration} min` : ''}</option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                );
              })()}

              {/* Duration control (both types, editable) */}
              {form.appointment_type && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                    <label style={{ ...labelStyle, marginBottom: 0 }}>Expected duration</label>
                    {startMin24 != null && (
                      <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: isDark ? '#5eead4' : '#0d9488' }}>
                        ends ~{fmtMin(startMin24 + effectiveDuration)}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', borderRadius: '0.625rem', overflow: 'hidden', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}` }}>
                      <button type="button" onClick={() => setDuration(effectiveDuration - 5)} style={durStepBtn}>−</button>
                      <span style={{ padding: '0 0.625rem', minWidth: '58px', textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>{effectiveDuration} min</span>
                      <button type="button" onClick={() => setDuration(effectiveDuration + 5)} style={durStepBtn}>+</button>
                    </div>
                    <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                      {(form.appointment_type === 'CONSULTATION' ? [5, 10, 15] : [30, 45, 60, 90]).map((d) => {
                        const on = effectiveDuration === d;
                        return (
                          <button key={d} type="button" onClick={() => setDuration(d)} style={{
                            padding: '0.3125rem 0.625rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
                            border: on ? '2px solid #0d9488' : `1.5px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                            background: on ? '#0d9488' : (isDark ? 'rgba(255,255,255,0.05)' : '#ffffff'),
                            color: on ? '#fff' : (isDark ? '#d4d4d8' : '#374151'),
                          }}>{d}m</button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Day timeline picker — date lives ON the calendar header ── */}
              {form.doctor_member_id && (
                <div>
                  {/* Prominent, editable date for the booking */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem',
                    padding: '0.5rem 0.625rem', borderRadius: '0.625rem',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                    background: isDark ? 'rgba(13,148,136,0.08)' : 'rgba(13,148,136,0.04)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <DatePicker
                        mode="single"
                        value={form.appointment_date}
                        min={todayISO()}
                        isDark={isDark}
                        onChange={(iso) => handleChange('appointment_date', iso)}
                      />
                      {form.appointment_date === todayISO() && (
                        <span style={{ fontSize: '0.625rem', color: '#0d9488', fontWeight: 600 }}>📌 Today</span>
                      )}
                    </div>
                    <span style={{ fontSize: '0.6875rem', color: isDark ? 'rgba(255,255,255,0.45)' : '#9ca3af', fontWeight: 500 }}>
                      {selectedDoctorLabel}
                    </span>
                  </div>

                  <label style={{ ...labelStyle, marginBottom: '0.375rem' }}>Pick a time — tap an open spot, drag to resize</label>
                  <DayTimeline
                    appointments={timelineAppointments}
                    date={form.appointment_date}
                    isDark={isDark}
                    mode="pick"
                    selectedTime={resolvedTime24}
                    proposedDurationMinutes={effectiveDuration}
                    patientLabel={patientName || 'New patient'}
                    onPickTime={handlePickTime}
                    onResizeDuration={setDuration}
                    onClashChange={setClashAppt}
                  />
                  {clashAppt && !pastTimeError && (
                    <div style={{
                      marginTop: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                      padding: '0.5rem 0.75rem', borderRadius: '0.625rem',
                      border: `1px solid ${isDark ? 'rgba(245,158,11,0.3)' : 'rgba(245,158,11,0.35)'}`,
                      background: isDark ? 'rgba(245,158,11,0.1)' : '#fffbeb',
                    }}>
                      <span style={{ fontSize: '0.8125rem' }}>⚠️</span>
                      <span style={{ fontSize: '0.75rem', color: isDark ? '#fcd34d' : '#92400e', fontWeight: 500 }}>
                        Overlaps {clashAppt.appointment_type === 'BLOCK' ? `${clashAppt.label || 'a block'} (blocked)` : (clashAppt.patient_name || 'another appointment')}
                        {clashAppt.appointment_time && !(clashAppt.appointment_type === 'BLOCK' && clashAppt.all_day) ? ` (until ~${clashEndLabel(clashAppt)})` : ''}. Overlap is allowed — book if intended.
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Manual time fine-tune (the timeline above is the primary picker) */}
              <div>
                <label style={labelStyle}>Appointment Time * <span style={{ fontWeight: 400, textTransform: 'none' }}>(or type it)</span></label>
                <SmartTimeInput
                  value={form.timeRaw}
                  onChange={(raw) => handleChange('timeRaw', raw)}
                  onResolve={handleTimeResolve}
                  isDark={isDark}
                  placeholder="e.g. 3:30, 10, 2pm"
                />
                {pastTimeError && (
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.6875rem', color: '#ef4444', fontWeight: 500 }}>⏰ {pastTimeError}</p>
                )}
                {validationErrors.timeRaw && !pastTimeError && <p style={errorTextStyle}>{validationErrors.timeRaw}</p>}
              </div>

              {/* ── Today's Queue Count (only when booking for today) ── */}
              {todayQueueCount !== null && form.appointment_date === todayISO() && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0.875rem',
                  borderRadius: '0.625rem',
                  background: isDark ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.04)',
                  border: `1px solid ${isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)'}`,
                }}>
                  <span style={{ fontSize: '0.8125rem' }}>🏥</span>
                  <span style={{ fontSize: '0.75rem', color: isDark ? '#93c5fd' : '#2563eb', fontWeight: 500 }}>
                    {todayQueueCount} patient{todayQueueCount !== 1 ? 's' : ''} currently in queue today
                    {todayQueueCount >= 10 && <span style={{ color: '#f59e0b' }}> — clinic is busy</span>}
                  </span>
                </div>
              )}

              {/* Notes */}
              <div>
                <label style={labelStyle}>Notes <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                <textarea
                  value={form.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Any special notes..."
                  rows={2}
                  style={{ ...inputBase(false), resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              {/* Submit */}
              {editingId ? (
                <div>
                  <button type="submit" disabled={loading || !!pastTimeError} style={btnPrimary(loading || !!pastTimeError)}>
                    {loading ? <Spinner /> : null}
                    {loading ? 'Saving...' : '💾 Save changes'}
                  </button>
                  <button type="button" onClick={cancelEdit} style={{
                    margin: '0.5rem auto 0', display: 'block', background: 'none', border: 'none',
                    color: isDark ? '#9ca3af' : '#6b7280', fontSize: '0.75rem', cursor: 'pointer',
                  }}>Cancel edit</button>
                </div>
              ) : existingForPatient.length > 0 ? (
                <div>
                  <button type="button" disabled style={btnPrimary(true)}>
                    📅 Already booked with this doctor today
                  </button>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.6875rem', color: isDark ? '#9ca3af' : '#6b7280', textAlign: 'center' }}>
                    Edit or cancel the existing appointment above — or pick a different date/doctor to book another.
                  </p>
                </div>
              ) : (
                <button type="submit" disabled={loading || !!pastTimeError} style={btnPrimary(loading || !!pastTimeError)}>
                  {loading ? <Spinner /> : null}
                  {loading ? 'Booking...' : '📅 Book Appointment'}
                </button>
              )}
            </form>
          )}
        </div>
      </div>
    </>
  );
}

// ── Success Popup ────────────────────────────────────────────────────

function SuccessPopup({ appointment, isDark, onClose, isScoped }) {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!appointment) return;
    if (countdown <= 0) {
      onClose();
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, onClose, appointment]);

  if (!appointment) return null;

  const apptNumber = appointment.appointment_number || '—';
  const apptDate = appointment.appointment_date || '';
  const apptTime = appointment.appointment_time || '';
  const patientName = appointment.patient_name || '';
  const doctorName = appointment.doctor_name || '';
  const mobile = appointment.patient_mobile || '';

  let displayTime = apptTime;
  try {
    const match = /^(\d{1,2}):(\d{2})/.exec(apptTime);
    if (match) {
      const h = parseInt(match[1], 10);
      const m = match[2];
      const period = h >= 12 ? 'PM' : 'AM';
      displayTime = `${h % 12 || 12}:${m} ${period}`;
    }
  } catch (_) {}

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: isDark ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.5)',
      padding: '1rem',
      overflowY: 'auto',
    }}>
      {/* ✕ Close — floating outside the card */}
      <button onClick={onClose} style={{
        position: 'fixed', top: '1rem', right: '1rem', zIndex: 10000,
        background: isDark ? '#374151' : '#ffffff',
        border: `2px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
        color: isDark ? '#f5f5f7' : '#374151',
        cursor: 'pointer', fontSize: '1.25rem', fontWeight: 700,
        width: '2.75rem', height: '2.75rem', borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.5)' : '0 4px 16px rgba(0,0,0,0.15)',
        transition: 'background 0.15s, transform 0.15s',
      }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        title="Close"
      >✕</button>

      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: '480px', borderRadius: '1.5rem',
        background: isDark ? '#1a1a22' : '#ffffff',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0d9488 0%, #0d9488 50%, #2dd4bf 100%)',
          padding: '2rem 1.5rem 1.75rem', textAlign: 'center',
          borderRadius: '1.5rem 1.5rem 0 0',
        }}>
          <div style={{
            width: '4rem', height: '4rem', margin: '0 auto 1rem', borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '2rem',
          }}>📅</div>
          <h3 style={{ margin: 0, color: '#fff', fontSize: '1.375rem', fontWeight: 700 }}>
            Appointment Booked!
          </h3>
        </div>

        {/* Appointment Number Badge */}
        <div style={{ textAlign: 'center', padding: '1.75rem 1.5rem 1.25rem' }}>
          <p style={{
            margin: 0, fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.12em', color: isDark ? 'rgba(255,255,255,0.4)' : '#9ca3af',
          }}>Appointment #</p>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginTop: '0.75rem', padding: '0.75rem 2.5rem', borderRadius: '1rem',
            background: isDark ? 'rgba(13, 148, 136,0.1)' : 'rgba(13, 148, 136,0.06)',
            border: `2px solid ${isDark ? 'rgba(13, 148, 136,0.25)' : 'rgba(13, 148, 136,0.15)'}`,
          }}>
            <span style={{
              fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
              fontWeight: 800,
              fontFamily: '"SF Mono", "Cascadia Code", "Fira Code", "Consolas", monospace',
              background: 'linear-gradient(135deg, #0d9488, #0d9488)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              lineHeight: 1.2, whiteSpace: 'nowrap',
            }}>{apptNumber}</span>
          </div>
        </div>

        {/* Details */}
        <div style={{ padding: '0 1.5rem 1.25rem' }}>
          <div style={{
            borderRadius: '0.75rem',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            background: isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb', overflow: 'hidden',
          }}>
            <PopupRow label="Patient" value={patientName} mobile={mobile} isDark={isDark} />
            <PopupRow label="Doctor" value={doctorName} isDark={isDark} border />
            <PopupRow label="Date" value={apptDate} isDark={isDark} border />
            <PopupRow label="Time" value={displayTime} isDark={isDark} border />
          </div>
        </div>

        {/* Close + Auto-redirect */}
        <div style={{ padding: '0 1.5rem 1.75rem' }}>
          <button onClick={onClose} style={{
            width: '100%', padding: '1rem', borderRadius: '0.75rem', border: 'none',
            background: 'linear-gradient(135deg, #0d9488, #0d9488)', color: '#fff',
            fontWeight: 600, fontSize: '1rem', cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(13, 148, 136,0.35)',
          }}>
            {isScoped ? 'Close & Go to Dashboard' : 'Close & Book Next Appointment'}
          </button>
          <p style={{
            textAlign: 'center', margin: '0.75rem 0 0', fontSize: '0.8125rem',
            color: isDark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
          }}>
            Auto-closing in <span style={{ fontWeight: 700, color: '#0d9488' }}>{countdown}s</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function PopupRow({ label, value, mobile, isDark, border }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0.75rem 1rem',
      borderTop: border ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` : 'none',
    }}>
      <span style={{ fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280' }}>{label}</span>
      <span style={{ fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        {value}
        {mobile && <PhoneButton phoneNumber={mobile} isDark={isDark} size="sm" />}
      </span>
    </div>
  );
}

// ── Spinner ──────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg className="animate-spin" style={{ width: '1rem', height: '1rem' }} viewBox="0 0 24 24" fill="none">
      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}
